// Event Registration Modal functionality
(function () {
  "use strict";

  // Get current locale
  const currentLocale = document.documentElement.lang || "en";
  const isNorwegian = currentLocale === "nb" || currentLocale === "no";

  // Helper function to show form status messages
  function showFormStatus(statusDiv, type, message) {
    if (!statusDiv) return;

    statusDiv.className = "event-registration-form__status show " + type;
    statusDiv.textContent = message;
    statusDiv.style.display = "block";
    statusDiv.style.visibility = "visible";
    statusDiv.style.opacity = "1";

    // Scroll into view after a small delay to ensure DOM is updated
    setTimeout(() => {
      statusDiv.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  }

  /**
   * Add subscriber to Mailchimp using AJAX Form API
   * This uses Mailchimp's JSONP endpoint - no API key needed
   */
  function addToMailchimp(formData) {
    return new Promise((resolve) => {
      // Mailchimp configuration - set in theme.liquid
      const mailchimpConfig = window.mailchimpConfig || {};
      const userId = mailchimpConfig.userId;
      const formId = mailchimpConfig.formId;
      const dataCenter = mailchimpConfig.dataCenter || "us12";

      if (!userId || !formId) {
        console.log("Mailchimp not configured (missing userId or formId)");
        resolve({ skipped: true });
        return;
      }

      // Split name for Mailchimp merge fields
      const nameParts = (formData.name || "").trim().split(" ");
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";

      // Build Mailchimp AJAX endpoint
      const endpoint = `https://${dataCenter}.list-manage.com/subscribe/post-json`;
      const callbackName = `mailchimpCallback_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      // Create URL parameters
      // Only sending: Email, First Name, Last Name, and Phone
      const params = new URLSearchParams({
        u: userId,
        id: formId,
        c: callbackName,
        EMAIL: formData.email || "",
        FNAME: firstName,
        LNAME: lastName,
        PHONE: formData.phone || "",
      });

      // Set up JSONP callback
      let timeoutCleared = false;
      const timeout = setTimeout(() => {
        if (!timeoutCleared && window[callbackName]) {
          delete window[callbackName];
          const scriptTag = document.getElementById(
            `mailchimp-script-${callbackName}`
          );
          if (scriptTag) scriptTag.remove();
          resolve({ error: "Request timeout" });
        }
      }, 10000);

      window[callbackName] = function (response) {
        timeoutCleared = true;
        clearTimeout(timeout);

        // Clean up
        delete window[callbackName];
        const scriptTag = document.getElementById(
          `mailchimp-script-${callbackName}`
        );
        if (scriptTag) scriptTag.remove();

        if (response && response.result === "success") {
          console.log("Mailchimp subscription successful");
          resolve({ success: true, data: response });
        } else {
          const errorMsg =
            response && response.msg
              ? response.msg
              : "Mailchimp subscription failed";

          console.warn("Mailchimp error:", errorMsg);
          resolve({ error: errorMsg });
        }
      };

      // Create and append script tag for JSONP
      const script = document.createElement("script");
      script.id = `mailchimp-script-${callbackName}`;
      script.src = `${endpoint}?${params.toString()}`;
      script.async = true;

      script.onerror = function () {
        timeoutCleared = true;
        clearTimeout(timeout);
        if (window[callbackName]) delete window[callbackName];
        script.remove();
        resolve({ error: "Script load failed" });
      };

      document.head.appendChild(script);
    });
  }

  // Function to validate form fields
  function validateFormFields(form) {
    const nameInput = form.querySelector("#EventRegistrationForm-name");
    const phoneInput = form.querySelector("#EventRegistrationForm-contact");
    const emailInput = form.querySelector("#EventRegistrationForm-email");
    const errors = [];

    // Validate Name
    if (!nameInput || !nameInput.value.trim()) {
      errors.push({
        field: nameInput,
        message: isNorwegian ? "Navn er påkrevd" : "Name is required",
      });
    }

    // Validate Phone (must be numbers only, minimum 6 digits)
    if (!phoneInput || !phoneInput.value.trim()) {
      errors.push({
        field: phoneInput,
        message: isNorwegian ? "Kontakt er påkrevd" : "Contact is required",
      });
    } else {
      // Remove any non-numeric characters for validation
      const phoneDigits = phoneInput.value.replace(/\D/g, "");
      if (phoneDigits.length < 6) {
        errors.push({
          field: phoneInput,
          message: isNorwegian
            ? "Kontaktnummer må være minst 6 siffer"
            : "Contact number must be at least 6 digits",
        });
      } else {
        // Update the input value to only contain digits
        phoneInput.value = phoneDigits;
      }
    }

    // Validate Email
    if (!emailInput || !emailInput.value.trim()) {
      errors.push({
        field: emailInput,
        message: isNorwegian ? "E-post er påkrevd" : "Email is required",
      });
    } else {
      // HTML5 email validation
      if (!emailInput.validity.valid) {
        errors.push({
          field: emailInput,
          message: isNorwegian
            ? "Vennligst oppgi en gyldig e-postadresse"
            : "Please enter a valid email address",
        });
      }
    }

    return errors;
  }

  // Function to handle Google Sheets form submission
  function handleGoogleSheetsSubmission(e) {
    e.preventDefault();

    const form = e.target;
    const statusDiv = form.querySelector("#EventRegistrationForm-status");
    const consentCheckbox = form.querySelector(
      "#EventRegistrationForm-consent"
    );
    const consentErrorDiv = form.querySelector(
      "#EventRegistrationForm-consent-error"
    );

    // Clear any previous errors
    if (statusDiv) {
      statusDiv.className = "event-registration-form__status";
      statusDiv.textContent = "";
      statusDiv.style.display = "none";
    }
    if (consentErrorDiv) {
      consentErrorDiv.classList.remove("show");
      consentErrorDiv.textContent = "";
    }

    // Remove previous error styling from fields
    const allFields = form.querySelectorAll(".field__input");
    allFields.forEach((field) => {
      field.classList.remove("field__input--error");
    });

    // Validate form fields
    const fieldErrors = validateFormFields(form);
    if (fieldErrors.length > 0) {
      // Show first error in status div
      showFormStatus(statusDiv, "error", fieldErrors[0].message);

      // Add error styling to invalid fields
      fieldErrors.forEach((error) => {
        if (error.field) {
          error.field.classList.add("field__input--error");
          error.field.focus();
        }
      });

      return;
    }

    // Validate consent checkbox
    if (consentCheckbox && !consentCheckbox.checked) {
      const errorMessage = isNorwegian
        ? "Du må samtykke for å fortsette"
        : "You must consent to continue";

      // Show error below checkbox
      if (consentErrorDiv) {
        consentErrorDiv.textContent = errorMessage;
        consentErrorDiv.classList.add("show");
        consentErrorDiv.style.display = "block";
        setTimeout(() => {
          consentErrorDiv.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 100);
      }
      return;
    }

    // Collect form data
    const formData = new FormData(form);
    const data = {
      timestamp: new Date().toISOString(),
      name: formData.get("name"),
      phone: formData.get("phone"),
      email: formData.get("email"),
      city: formData.get("city"),
      eventTitle: formData.get("eventTitle"),
      eventTime: formData.get("eventTime"),
      eventDate: formData.get("eventDate"),
      eventPlace: formData.get("eventPlace"),
      consent: consentCheckbox ? consentCheckbox.checked : false,
    };

    // Google Apps Script URL - must be set via window variable
    const googleScriptUrl = window.googleSheetsWebhookUrl || null;

    if (!googleScriptUrl) {
      showFormStatus(
        statusDiv,
        "error",
        "Configuration error: Google Sheets URL not set."
      );
      console.error("Google Sheets webhook URL not configured");
      return;
    }

    // Show loading state
    const submitBtn = form.querySelector('button[type="submit"]');
    const originalText = submitBtn.textContent;
    submitBtn.disabled = true;
    submitBtn.textContent = isNorwegian ? "Sender..." : "Sending...";

    // Send data to Google Apps Script
    fetch(googleScriptUrl, {
      method: "POST",
      mode: "no-cors",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((response) => {
        // Add subscriber to Mailchimp (runs asynchronously, won't block success flow)
        addToMailchimp(data)
          .then((mailchimpResult) => {
            if (mailchimpResult.success) {
              console.log("Mailchimp: Subscriber added successfully");
            } else if (!mailchimpResult.skipped) {
              console.warn(
                "Mailchimp: Failed to add subscriber",
                mailchimpResult.error
              );
            }
          })
          .catch((error) => {
            console.error("Mailchimp error:", error);
          });

        // Hide event overview details
        const overviewDiv = form.querySelector(
          "#EventRegistrationForm-overview"
        );
        if (overviewDiv) {
          overviewDiv.style.display = "none";
        }

        // Hide form fields
        const fieldsWrapper = form.querySelector(
          "#EventRegistrationForm-fields"
        );
        if (fieldsWrapper) {
          fieldsWrapper.style.display = "none";
        }

        // Show success message
        const successDiv = form.querySelector("#EventRegistrationForm-success");
        if (successDiv) {
          successDiv.style.display = "block";

          // Handle close button
          const closeBtn = successDiv.querySelector(
            "#EventRegistrationForm-close-btn"
          );
          if (closeBtn) {
            closeBtn.addEventListener("click", function () {
              // Reset form state
              const overviewDiv = form.querySelector(
                "#EventRegistrationForm-overview"
              );
              if (overviewDiv) {
                overviewDiv.style.display = "block";
              }

              const fieldsWrapper = form.querySelector(
                "#EventRegistrationForm-fields"
              );
              if (fieldsWrapper) {
                fieldsWrapper.style.display = "block";
              }

              // Hide success message
              if (successDiv) {
                successDiv.style.display = "none";
              }

              // Clear form values
              form.reset();

              // Close modal
              const modal = form.closest("modal-dialog");
              if (modal) {
                if (modal.hide) {
                  modal.hide();
                } else {
                  modal.removeAttribute("open");
                  document.body.classList.remove("overflow-hidden");
                }
              }
            });
          }
        }

        // Reset form
        form.reset();
      })
      .catch((error) => {
        console.error("Submission error:", error);
        showFormStatus(
          statusDiv,
          "error",
          isNorwegian
            ? "Det oppstod en feil. Vennligst prøv igjen."
            : "An error occurred. Please try again."
        );
      })
      .finally(() => {
        submitBtn.disabled = false;
        submitBtn.textContent = originalText;
      });
  }

  // Function to open modal with event data
  window.openEventRegistrationModal = function (button) {
    const modal = document.getElementById("EventRegistrationModal");
    if (!modal) {
      console.error("Event registration modal not found");
      return;
    }

    // Get event data from button data attributes
    const eventTitle = isNorwegian
      ? button.dataset.eventTitleNo || button.dataset.eventTitleEn
      : button.dataset.eventTitleEn || button.dataset.eventTitleNo;

    const eventDescription = isNorwegian
      ? button.dataset.eventDescriptionNo || button.dataset.eventDescriptionEn
      : button.dataset.eventDescriptionEn || button.dataset.eventDescriptionNo;

    const eventTime = isNorwegian
      ? button.dataset.eventTimeNo || button.dataset.eventTimeEn
      : button.dataset.eventTimeEn || button.dataset.eventTimeNo;

    const eventDate = isNorwegian
      ? button.dataset.eventDateNo || button.dataset.eventDateEn
      : button.dataset.eventDateEn || button.dataset.eventDateNo;

    const eventPlace = isNorwegian
      ? button.dataset.eventPlaceNo || button.dataset.eventPlaceEn
      : button.dataset.eventPlaceEn || button.dataset.eventPlaceNo;

    // Populate modal with event data
    const titleElement = modal.querySelector("#EventRegistrationModal-title");
    const descriptionElement = modal.querySelector(
      ".event-registration-modal__description"
    );
    const timeValueElement = modal.querySelector("#event-time-value");
    const dateValueElement = modal.querySelector("#event-date-value");
    const placeValueElement = modal.querySelector("#event-place-value");
    const overviewElement = modal.querySelector("#event-overview-value");
    const hiddenTitleInput = modal.querySelector("#event-title-hidden");
    const hiddenTimeInput = modal.querySelector("#event-time-hidden");
    const hiddenDateInput = modal.querySelector("#event-date-hidden");
    const hiddenPlaceInput = modal.querySelector("#event-place-hidden");

    const eventOverview = isNorwegian
      ? button.dataset.eventOverviewNo || button.dataset.eventOverviewEn
      : button.dataset.eventOverviewEn || button.dataset.eventOverviewNo;

    if (titleElement) {
      titleElement.textContent = eventTitle || "";
    }

    if (descriptionElement) {
      descriptionElement.innerHTML = eventDescription || "";
    }

    if (timeValueElement) {
      timeValueElement.textContent = eventTime || "";
    }

    if (dateValueElement) {
      dateValueElement.textContent = eventDate || "";
    }

    if (placeValueElement) {
      placeValueElement.textContent = eventPlace || "";
    }

    if (overviewElement) {
      overviewElement.innerHTML = eventOverview || "";
    }

    if (hiddenTitleInput) {
      hiddenTitleInput.value = eventTitle || "";
    }

    if (hiddenTimeInput) {
      hiddenTimeInput.value = eventTime || "";
    }

    if (hiddenDateInput) {
      hiddenDateInput.value = eventDate || "";
    }

    if (hiddenPlaceInput) {
      hiddenPlaceInput.value = eventPlace || "";
    }

    // Show modal using the ModalDialog component
    // First, ensure form is in proper state
    const form = modal.querySelector("#EventRegistrationForm");
    if (form) {
      form.classList.remove("form--success");
      const statusDiv = form.querySelector("#EventRegistrationForm-status");
      if (statusDiv) {
        statusDiv.className = "event-registration-form__status";
        statusDiv.textContent = "";
      }
    }

    // Open modal manually to avoid trapFocus issues
    modal.setAttribute("open", "");
    document.body.classList.add("overflow-hidden");

    // Focus on first text input (exclude checkbox)
    const firstInput = modal.querySelector(
      "input[type='text'], input[type='email'], input[type='tel']"
    );
    if (firstInput && firstInput.focus) {
      try {
        setTimeout(() => firstInput.focus(), 100);
      } catch (err) {
        console.error("Error focusing input:", err);
      }
    }
  };

  // Initialize modal close handlers and form submission
  document.addEventListener("DOMContentLoaded", function () {
    const modal = document.getElementById("EventRegistrationModal");
    if (!modal) return;

    const closeButton = modal.querySelector("#ModalClose-EventRegistration");

    // Close button handler
    if (closeButton) {
      closeButton.addEventListener("click", function () {
        if (modal.hide) {
          modal.hide();
        } else {
          modal.removeAttribute("open");
          document.body.classList.remove("overflow-hidden");
        }
      });
    }

    // Close on outside click
    modal.addEventListener("click", function (e) {
      if (
        e.target === modal ||
        e.target.classList.contains("event-registration-modal__overlay")
      ) {
        if (modal.hide) {
          modal.hide();
        } else {
          modal.removeAttribute("open");
          document.body.classList.remove("overflow-hidden");
        }
      }
    });

    // Close on Escape key
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && modal.hasAttribute("open")) {
        if (modal.hide) {
          modal.hide();
        } else {
          modal.removeAttribute("open");
          document.body.classList.remove("overflow-hidden");
        }
      }
    });

    // Handle Google Sheets form submission
    const form = modal.querySelector("#EventRegistrationForm");
    if (form) {
      form.addEventListener("submit", handleGoogleSheetsSubmission);

      // Real-time phone number validation - only allow digits
      const phoneInput = form.querySelector("#EventRegistrationForm-contact");
      if (phoneInput) {
        phoneInput.addEventListener("input", function (e) {
          // Remove any non-numeric characters
          const value = e.target.value.replace(/\D/g, "");
          if (e.target.value !== value) {
            e.target.value = value;
          }
          // Remove error styling when user starts typing
          if (value.length > 0) {
            e.target.classList.remove("field__input--error");
          }
        });

        // Also handle paste events
        phoneInput.addEventListener("paste", function (e) {
          e.preventDefault();
          const paste = (e.clipboardData || window.clipboardData).getData(
            "text"
          );
          const digitsOnly = paste.replace(/\D/g, "");
          e.target.value = digitsOnly;
          // Trigger input event to update value
          e.target.dispatchEvent(new Event("input"));
        });
      }

      // Remove error styling when user starts typing in other fields
      const nameInput = form.querySelector("#EventRegistrationForm-name");
      const emailInput = form.querySelector("#EventRegistrationForm-email");

      if (nameInput) {
        nameInput.addEventListener("input", function (e) {
          if (e.target.value.trim().length > 0) {
            e.target.classList.remove("field__input--error");
          }
        });
      }

      if (emailInput) {
        emailInput.addEventListener("input", function (e) {
          if (e.target.value.trim().length > 0) {
            e.target.classList.remove("field__input--error");
          }
        });
      }

      // Also add click listener to submit button as backup
      const submitBtn = form.querySelector("button[type='submit']");
      if (submitBtn) {
        submitBtn.addEventListener("click", function (e) {
          e.preventDefault();
          handleGoogleSheetsSubmission({
            preventDefault: () => {},
            target: form,
          });
        });
      }
    }
  });
})();
